'use client';
import {Box} from '@mui/material';
import {styled, type Theme} from '@mui/material/styles';
import Image, {type ImageProps} from 'next/image';
import type {ReactNode} from 'react';
import {useTranslation} from 'react-i18next';
import {Typography} from '../primitives';

export type ListingEmptyStateTone = 'default' | 'inverse' | 'background';

export interface ListingEmptyStateProps {
  titleKey?: string;
  bodyKey?: string;
  icon: ReactNode;
  illustration?: ImageProps['src'];
  tone?: ListingEmptyStateTone;
  card?: boolean;
}

// Card fills the slot the results grid would occupy — roughly one banner/card row.
const CARD_MIN_HEIGHT_MOBILE = 240;
const CARD_MIN_HEIGHT_DESKTOP = 360;
// Standalone (grid-empty) reserves the full area the results grid would fill and
// centers its content — matches the Figma empty-state section (~596px tall, block
// centered) so the empty state reads as a large, intentional state, not a stub.
const STANDALONE_MIN_HEIGHT_MOBILE = 380;
const STANDALONE_MIN_HEIGHT_DESKTOP = 560;

const Root = styled(Box, {shouldForwardProp: prop => prop !== '$card' && prop !== '$tone'})<{
  $card: boolean;
  $tone: ListingEmptyStateTone;
}>(({theme, $card, $tone}) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: theme.spacing(2),
  textAlign: 'center',
  ...(!$card && {
    width: '100%',
    minHeight: STANDALONE_MIN_HEIGHT_MOBILE,
    paddingBlock: theme.spacing(8),
    [theme.breakpoints.up('md')]: {minHeight: STANDALONE_MIN_HEIGHT_DESKTOP},
  }),
  ...($card && {
    width: '100%',
    minHeight: CARD_MIN_HEIGHT_MOBILE,
    paddingInline: theme.spacing(2),
    paddingBlock: theme.spacing(6),
    borderRadius: theme.radii.md,
    background: {
      inverse: theme.palette.colors.surface.neutral.inverseMuted,
      background: theme.palette.colors.surface.neutral.page,
      default: theme.palette.colors.surface.neutral.default,
    }[$tone],
    [theme.breakpoints.up('md')]: {minHeight: CARD_MIN_HEIGHT_DESKTOP},
  }),
}));

const IconBox = styled(Box, {shouldForwardProp: prop => prop !== '$tone'})<{
  $tone: ListingEmptyStateTone;
}>(({theme, $tone}) => {
  const {surface, icon} = theme.palette.colors;
  return {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 70,
    height: 70,
    fontSize: 32,
    [theme.breakpoints.up('md')]: {width: 80, height: 80, fontSize: 36},
    borderRadius: theme.radii.full,
    flexShrink: 0,
    ...{
      default: {background: surface.neutral.default, color: icon.neutral.muted},
      background: {background: surface.neutral.default, color: icon.neutral.muted},
      inverse: {background: surface.neutral.inverseSubtle, color: icon.neutral.inverse},
    }[$tone],
  };
});

const EMPTY_TEXT: Record<ListingEmptyStateTone, (theme: Theme) => {title: string; body: string}> = {
  default: theme => ({title: theme.palette.colors.text.neutral.strong, body: theme.palette.colors.text.neutral.default}),
  background: theme => ({title: theme.palette.colors.text.neutral.strong, body: theme.palette.colors.text.neutral.default}),
  inverse: theme => ({title: theme.palette.colors.text.neutral.inverse, body: theme.palette.colors.text.neutral.inverse}),
};

export function ListingEmptyState({titleKey, bodyKey, icon, illustration, tone = 'default', card = false}: ListingEmptyStateProps) {
  const {t} = useTranslation();
  const inverse = tone === 'inverse';
  return (
    <Root $card={card} $tone={tone}>
      {illustration && !inverse ? (
        <Image src={illustration} alt="" width={120} height={120} style={{objectFit: 'contain'}} />
      ) : (
        <IconBox $tone={tone}>{icon}</IconBox>
      )}
      <Box sx={{display: 'flex', flexDirection: 'column', gap: 0.5}}>
        {titleKey && (
          <Typography
            size="title-5"
            weight="bold"
            family="primary"
            mobileSize="body-1"
            mobileWeight="bold"
            as="h3"
            sx={{
              textTransform: {xs: 'none', md: 'capitalize'},
              color: theme => EMPTY_TEXT[tone](theme).title,
            }}
          >
            {t(titleKey)}
          </Typography>
        )}
        {bodyKey && (
          <Typography
            size="body-2"
            weight="regular"
            lineHeight="22"
            sx={{
              color: theme => EMPTY_TEXT[tone](theme).body,
            }}
          >
            {t(bodyKey)}
          </Typography>
        )}
      </Box>
    </Root>
  );
}

ListingEmptyState.displayName = 'ListingEmptyState';
